import React, { useEffect, useMemo, useState } from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { TranslateFunction } from 'react-localize-redux';

import { List, Snackbar, Typography } from '../../../../../components/atoms';
import { ListItem } from '../../../../../components/atoms/List';
import { ChevronRight as ChevronRightIcon } from '../../../../../components/icons';
import { Dialog, LoadingSpinner } from '../../../../../components/layouts';
import {
  AcceptedRestaurantVoucher,
  AddUserPayGreenVoucher,
  VoucherStatus,
} from '../../../../../core/state/payment/payment.types';
import { Track } from '../../../../../weborder/analytics';
import { DispatchActions } from '../index';
import { restaurantVoucherUtils } from '../utils';

type Props = {
  actions: DispatchActions;
  handleCloseDialog: () => void;
  isDialogOpen: boolean;
  setVoucherStatus: (status: VoucherStatus) => void;
  storeId: number;
  translate: TranslateFunction;
  voucherOptions?: AcceptedRestaurantVoucher[];
};

const useStyles = makeStyles((theme: Theme) => ({
  dialogContent: {
    minHeight: '500px',
    paddingTop: theme.spacing(3),
  },

  iFrame: {
    border: 'none',
    height: 0,
    width: 0,
  },

  iFrameContainer: {
    display: 'flex',
    justifyContent: 'center',
  },

  iFrameLoaded: {
    minHeight: '500px',
    minWidth: '320px',
  },

  loadingPanel: {
    minHeight: '500px',
  },

  title: {
    paddingBottom: theme.spacing(0.5),
  },

  voucherOption: {
    alignItems: 'center',
    display: 'flex',
    minHeight: '56px',
  },
}));

export function AddVoucherDialog(props: Props): JSX.Element {
  const classes = useStyles();
  const {
    actions,
    handleCloseDialog,
    isDialogOpen,
    setVoucherStatus,
    storeId,
    translate,
    voucherOptions = [],
  } = props;

  const [selectedVoucher, setSelectedVoucher] =
    useState<AddUserPayGreenVoucher>(null);
  const [isIFrameLoading, setIsIFrameLoading] = useState<boolean>(false);

  const showVoucherList =
    voucherOptions?.length > 0 && !selectedVoucher && !isIFrameLoading;
  const showPayGreenIFrame = !!selectedVoucher;
  const showWarningMessage = voucherOptions?.length === 0;

  useEffect(() => {
    if (!isDialogOpen) {
      setSelectedVoucher(null);
    }
  }, [isDialogOpen]);

  const handleOnAddVoucher = (
    _event: React.MouseEvent<HTMLElement>,
    voucher: AcceptedRestaurantVoucher,
  ): void => {
    Track('Payments', 'Add New Restaurant Voucher Start');

    setIsIFrameLoading(true);

    const payGreenVoucherScheme = voucher.VoucherScheme;
    actions
      .addUserPayGreenVoucher(payGreenVoucherScheme, storeId)
      .then((data) => {
        setSelectedVoucher(data);

        setVoucherStatus({
          isPolling: true,
          status: 'Pending',
          voucherId: data.PaymentAccountId,
        });
      })
      .catch(() => setSelectedVoucher(null));
  };

  const renderPayGreenIFrame = (): JSX.Element => {
    const iFrameStyles = clsx(
      classes.iFrame,
      !isIFrameLoading && classes.iFrameLoaded,
    );

    return (
      <div className={classes.iFrameContainer}>
        <iframe
          className={iFrameStyles}
          id="payGreenIFrame"
          src={`${selectedVoucher.UrlToRedirectTo}?display=insite`}
          title="PayGreen iFrame"
          onLoad={(): void => setIsIFrameLoading(false)}
        >
          {translate('RestaurantVoucher_iFrame_errorMessage')}
        </iframe>
      </div>
    );
  };

  const renderVoucherOption = (
    voucher: AcceptedRestaurantVoucher,
  ): JSX.Element => {
    const {
      Description: description,
      VoucherBrand: voucherBrand,
      VoucherScheme: voucherScheme,
    } = voucher;

    const voucherLogo = restaurantVoucherUtils.getVoucherLogo(
      description,
      false,
      'default',
      voucherBrand,
      voucherScheme,
    );

    return (
      <div
        aria-label={`${description} option`}
        className={classes.voucherOption}
      >
        {voucherLogo}
      </div>
    );
  };

  const renderVoucherOptions = useMemo(() => {
    const optionsMapped: ListItem[] = voucherOptions.map((option) => ({
      key: option.VoucherBrand,
      onClick: (event) => handleOnAddVoucher(event, option),
      primaryContent: renderVoucherOption(option),
      rightIcon: <ChevronRightIcon />,
    }));

    return optionsMapped;
  }, [voucherOptions.length]);

  return (
    <Dialog
      dialogName="restaurant-voucher-dialog"
      onTitleClick={handleCloseDialog}
      open={isDialogOpen}
    >
      <header className="fd-header">
        <Typography className={classes.title} variant="h1">
          {translate('RestaurantVoucher_addNewRestaurantVoucher')}
        </Typography>

        <Typography variant="caption">
          {translate('RestaurantVoucher_addNewRestaurantVoucher_helperText')}
        </Typography>
      </header>

      <article className="fd-content__main">
        <div className={classes.dialogContent}>
          {isIFrameLoading && (
            <LoadingSpinner className={classes.loadingPanel} variant="panel" />
          )}

          {showVoucherList && <List items={renderVoucherOptions} />}

          {showPayGreenIFrame && renderPayGreenIFrame()}

          {showWarningMessage && (
            <Snackbar
              message={`${translate(
                'RestaurantVoucher_cantAddVoucher_errorMessage',
              )}`}
              severity="warning"
            />
          )}
        </div>
      </article>
    </Dialog>
  );
}
